"use client";

import { useEffect } from "react";

/**
 * Dark-theme skin for the Fchat.vn webchat widget.
 *
 * Why a component instead of CSS in globals.css: the embed script appends a
 * `<chat-widget>` custom element that renders inside an **open shadow root**
 * (Vue `defineCustomElement`). Page-level CSS never crosses that boundary, so
 * the only way to restyle the widget is to inject a stylesheet into the shadow
 * root itself. Colours mirror `src/styles/theme.css` so the widget stops looking
 * like a white third-party popup on our dark site.
 *
 * The widget is loaded with `strategy="lazyOnload"` and renders a few seconds
 * later, hence the polling + MutationObserver (Vue re-renders parts of the tree
 * and could drop the injected tag).
 */

const STYLE_ID = "ecq-fchat-theme";

/* Tokens (see src/styles/theme.css):
   background #15181d · surface-raised #1f2428 · surface-alt #24272c
   surface-2 #3a3e44 · border-subtle #272a2e · text-default #d6d6d6
   primary #ec1938 · primary-400 #ff657c · neutral-400 #86878a */
const THEME_CSS = `
/* ---------- typography: the widget hardcodes Roboto everywhere ---------- */
.chat,
.chat_header,
.chat_field,
.preview-message,
.continue-text button,
.list-conversation .create-new-conv button,
.chat_form input,
.chat_form .message_form textarea,
.chat_form .message_form button {
  font-family: var(--font-avenir), "Avenir Next", ui-sans-serif, system-ui, sans-serif !important;
}

/* ---------- floating launcher ---------- */
#prime.fab {
  background: #ec1938 !important;
  box-shadow: 0 6px 20px rgba(236, 25, 56, 0.35), 0 0 4px rgba(0, 0, 0, 0.4) !important;
}

/* ---------- greeting bubble shown above the launcher ---------- */
.preview-message .message-item span {
  background: #1f2428 !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px !important;
  padding: 10px 14px !important;
  text-align: left !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.55) !important;
}

.preview-message .close {
  background: #24272c !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.6) !important;
}

/* ---------- quick-reply pills (bubble + conversation) ---------- */
.chat .quick-reply a,
.preview-message .quick-reply a {
  background: rgba(255, 255, 255, 0.04) !important;
  border: 1px solid #3a3e44 !important;
  color: #d6d6d6 !important;
  box-shadow: none !important;
}

.chat .quick-reply a:hover,
.preview-message .quick-reply a:hover {
  background: rgba(236, 25, 56, 0.14) !important;
  border-color: #ec1938 !important;
  color: #ffffff !important;
}

/* ---------- panel shell ---------- */
.chat {
  background: #15181d !important;
  border: 1px solid #272a2e !important;
  border-radius: 12px !important;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6) !important;
  color: #d6d6d6 !important;
}

.chat_header {
  background: #ec1938 !important;
  color: #ffffff !important;
  border-top-left-radius: 11px !important;
  border-top-right-radius: 11px !important;
}

#chat_head {
  color: #ffffff !important;
  font-weight: 600 !important;
}

.chat_option .agent {
  color: rgba(255, 255, 255, 0.85) !important;
}

.chat_converse,
.chat_converse.chat_form {
  background: transparent !important;
}

.chat_converse::-webkit-scrollbar {
  width: 8px;
}

.chat_converse::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 999px;
}

.chat_converse::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
}

/* ---------- message bubbles ---------- */
.chat .chat_converse .chat_msg_item.chat_msg_item_admin {
  background: #24272c !important;
  color: #d6d6d6 !important;
}

.chat .chat_converse .chat_msg_item.chat_msg_item_admin a {
  color: #ff657c !important;
}

.chat .chat_converse .chat_msg_item.chat_msg_item_admin:before {
  border-right-color: #24272c !important;
}

.chat .chat_converse .chat_msg_item.chat_msg_item_user {
  background: #ec1938 !important;
  color: #ffffff !important;
}

.chat .chat_converse .chat_msg_item ._btn_buttons {
  background-color: rgba(255, 255, 255, 0.06) !important;
  color: #ffffff !important;
}

.chat .chat_converse .chat_msg_item ._btn_buttons:hover {
  background-color: rgba(236, 25, 56, 0.16) !important;
}

.status,
.status2 {
  color: rgba(255, 255, 255, 0.45) !important;
}

/* ---------- "new conversation" screen ---------- */
.list-conversation .conversation_list_inner,
.channel_block .channel_block_inner {
  background: #1f2428 !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  color: #d6d6d6 !important;
}

.channel_block .block-title {
  color: #ffffff !important;
}

.list-conversation .create-new-conv button {
  background: #ec1938 !important;
  border-color: #ec1938 !important;
  color: #ffffff !important;
}

.channel_block .channel-item a {
  color: #d6d6d6 !important;
}

.channel_block .channel-item a:hover {
  color: #ff657c !important;
}

/* ---------- composer ---------- */
.fab_field {
  background: #181a1d !important;
  border-top: 1px solid #272a2e !important;
  border-bottom-left-radius: 11px !important;
  border-bottom-right-radius: 11px !important;
}

.chat_field,
.chat_field.chat_message {
  background: transparent !important;
  color: #ffffff !important;
}

.chat_field::placeholder {
  color: #78797d !important;
}

/* #fab_send gets an inline blue colour from the widget script. */
.fab_field .fab > i,
#fab_camera label i,
#fab_send i {
  color: #86878a !important;
}

.fab_field .fab > i:hover,
#fab_camera label i:hover,
#fab_send:hover i {
  color: #ec1938 !important;
}

/* ---------- burger menu popup ----------
   Left in place exactly as V1 shipped it, only re-skinned. Its two entries come from the
   API (getpage?page_id=…) as Fchat plan branding — "title": "Create Chatbot by
   Fchat.vn", "is_pro": 1 — so they are not ours to edit; app.js even re-appends
   is_pro items. Note the ☰ button doubles as the send button once the input has text,
   so it cannot be hidden either. */
.fchat-menu-show {
  background: #1f2428 !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6) !important;
  padding: 10px 0 !important;
}

.fchat-menu-show .arrow {
  background: #1f2428 !important;
}

.fchat-menu-show .menu-item a {
  color: #d6d6d6 !important;
}

.fchat-menu-show .menu-item a:hover {
  background-color: rgba(255, 255, 255, 0.08) !important;
  color: #ffffff !important;
}

/* ---------- emoji picker ---------- */
.emocat,
.emocat .visibleChar,
.emocat .visibleChar input {
  background: #1f2428 !important;
}

.emocat {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
}

.fchat-search-emoji input {
  background: rgba(255, 255, 255, 0.04) !important;
  border-color: #3a3e44 !important;
  color: #ffffff !important;
}

/* ---------- Fchat attribution (kept, just toned down) ---------- */
#power_by {
  color: #5b5b5f !important;
}

#power_by a {
  color: #86878a !important;
}
`;

const FchatTheme = () => {
  useEffect(() => {
    let observer: MutationObserver | null = null;
    let poll: ReturnType<typeof setInterval> | null = null;
    let giveUp: ReturnType<typeof setTimeout> | null = null;

    const inject = (root: ShadowRoot) => {
      if (root.querySelector(`#${STYLE_ID}`)) return;
      const style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = THEME_CSS;
      root.appendChild(style);
    };

    const stopPolling = () => {
      if (poll) {
        clearInterval(poll);
        poll = null;
      }
      if (giveUp) {
        clearTimeout(giveUp);
        giveUp = null;
      }
    };

    const attach = () => {
      const root = document.querySelector("chat-widget")?.shadowRoot;
      if (!root) return false;

      inject(root);
      // Vue swaps subtrees as the widget changes screens; re-add if it drops.
      observer = new MutationObserver(() => inject(root));
      observer.observe(root, { childList: true });
      return true;
    };

    if (!attach()) {
      poll = setInterval(() => {
        if (attach()) stopPolling();
      }, 300);
      // The widget is lazy-loaded; stop waiting if it never shows up.
      giveUp = setTimeout(stopPolling, 60000);
    }

    return () => {
      stopPolling();
      observer?.disconnect();
      document.querySelector("chat-widget")?.shadowRoot?.querySelector(`#${STYLE_ID}`)?.remove();
    };
  }, []);

  return null;
};

export default FchatTheme;
